import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Menu, Minus, Plus, X } from "lucide-react";
import { Link, useRoute } from "./router";
import { PORTAL_URL, ROUTES, SIGN_IN_URL, SIGN_UP_URL, SUPPORT_EMAIL, type Route } from "./links";

/** Opens the sample chore dialog from anywhere on the site. */
export const DemoContext = createContext<(phase?: "watch" | "try") => void>(() => {});
export const useDemo = () => useContext(DemoContext);

export function Brand({ small = false }: { small?: boolean }) {
  return (
    <Link className={"site-brand" + (small ? " small" : "")} href={ROUTES.home} aria-label="Sprout home">
      <img src="/plant-logo.svg" alt="" />
      <span>
        sprout<span className="site-brand-dot">.</span>
      </span>
    </Link>
  );
}

const NAV: { label: string; href: string; route?: Route; external?: boolean }[] = [
  { label: "How it works", href: "/#how-it-works" },
  { label: "Wall app", href: PORTAL_URL, external: true },
  { label: "Privacy", href: ROUTES.privacy, route: "privacy" },
  { label: "Pricing", href: ROUTES.pricing, route: "pricing" },
  { label: "FAQ", href: ROUTES.faq, route: "faq" },
];

function NavLinks({ route, onPick }: { route: Route; onPick?: () => void }) {
  return (
    <>
      {NAV.map((item) =>
        item.external ? (
          <a key={item.label} href={item.href}>
            {item.label}
          </a>
        ) : (
          <Link
            key={item.label}
            href={item.href}
            aria-current={item.route === route ? "page" : undefined}
            onClick={onPick}
          >
            {item.label}
          </Link>
        ),
      )}
    </>
  );
}

export function SiteHeader() {
  const route = useRoute();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [route]);
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);
  return (
    <header className="site-header">
      <div className="site-wrap site-header-row">
        <Brand />
        <nav className="site-nav" aria-label="Website">
          <NavLinks route={route} />
        </nav>
        <div className="site-header-actions">
          <a className="site-signin" href={SIGN_IN_URL}>
            <span className="long">Parent sign in</span>
            <span className="short">Sign in</span>
          </a>
          <a className="site-btn primary compact" href={SIGN_UP_URL}>
            Create a family
          </a>
          <button
            type="button"
            className="site-menu-btn"
            aria-label={open ? "Close menu" : "Menu"}
            aria-expanded={open}
            aria-controls="site-menu"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <nav id="site-menu" className="site-menu" aria-label="Website">
          <NavLinks route={route} onPick={() => setOpen(false)} />
          <a className="site-btn primary" href={SIGN_UP_URL}>
            Create a family
          </a>
        </nav>
      )}
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-wrap site-footer-row">
        <span className="site-footer-brand">
          <img src="/plant-logo.svg" alt="" />
          sprout.
        </span>
        <nav aria-label="Footer">
          <Link href={ROUTES.privacy}>Privacy</Link>
          <Link href={ROUTES.pricing}>Pricing</Link>
          <Link href={ROUTES.faq}>FAQ</Link>
          <a href={SIGN_IN_URL}>Parent sign in</a>
          <a href={PORTAL_URL}>Wall app</a>
        </nav>
        <span className="site-copy">© {new Date().getFullYear()} Sprout</span>
      </div>
    </footer>
  );
}

export function Overline({ children, center = false }: { children: ReactNode; center?: boolean }) {
  return (
    <div className={"site-overline" + (center ? " center" : "")}>
      <span aria-hidden="true" />
      {children}
    </div>
  );
}

export type QA = { q: string; a: ReactNode };

/** Native disclosure rows: keyboard and screen-reader friendly without extra script. */
export function Questions({
  items,
  openFirst = false,
  openAll = false,
}: {
  items: QA[];
  openFirst?: boolean;
  openAll?: boolean;
}) {
  return (
    <div className="site-questions">
      {items.map((item, i) => (
        <details key={item.q} open={openAll || (openFirst && i === 0)}>
          <summary>
            {item.q}
            <Plus className="when-closed" size={18} aria-hidden="true" />
            <Minus className="when-open" size={18} aria-hidden="true" />
          </summary>
          <div className="site-answer">{item.a}</div>
        </details>
      ))}
    </div>
  );
}

/** The fox card that closes the FAQ and privacy pages. */
export function HelpCard({ title, text, children }: { title: string; text: string; children?: ReactNode }) {
  return (
    <aside className="site-help">
      <img src="/fox.svg" alt="" />
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
      <div className="site-help-actions">
        {SUPPORT_EMAIL && (
          <a className="site-btn secondary" href={`mailto:${SUPPORT_EMAIL}`}>
            Write to us
          </a>
        )}
        {children}
      </div>
    </aside>
  );
}
